import React, { useState, useCallback } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import CpntForm from "../../components/forms/CpntForm";
import CpntFormSection from "../../components/forms/CpntFormSection";
import { useFetchPaysQuery, useFetchSecteurActiviteQuery, useFetchFonctionQuery } from "../../redux/slice/api/staticData";
import { getInscriptionSchema } from "./inscription/InscriptionSchema";
import CpntModalConvention from "./inscription/CpntModalConvention";
import CpntEmailVerification from "./inscription/CpntEmailVerification";
import CpntCodeVerification from "./inscription/CpntCodeVerification";
import CpntInscriptionForm from "./inscription/CpntInscriptionForm";
import { useLazyDemandeInscriptionEnvoiMailQuery, useDemandeInscriptionVerifyCodeMutation, useFetchDemandeInscriptionCreateMutation } from "../../redux/slice/api/FO/demandeInscriptionApi";
import { useLazyCompteLoginExistQuery } from "../../redux/slice/api/FO/compteApi";
import { IFormInscription } from "./inscription/types";
import { transformFormDataToAPI } from "./inscription/utils/apiTransformers";
import { prefillFormFromDemande } from "./inscription/utils/formHelpers";
import { useSirenValidation } from "../../hooks/useSirenValidation";
import keycloak from "../../keycloak";
import { FRANCE_ET_ASSIMILES_IDS } from "../../utils/helpers";

const PageInscription: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get("email");
  const [emailExists, setEmailExists] = useState(false);
  const [compteActif, setCompteActif] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [showCodeVerification, setShowCodeVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [isFranceOrAssimile, setIsFranceOrAssimile] = useState(false);
  const [showModalConvention, setShowModalConvention] = useState(false);
  const [sirenApiError, setSirenApiError] = useState<string | null>(null);
  const [idDemande, setIdDemande] = useState<number | null>(null);
  const [motifRefus, setMotifRefus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [demandeInscriptionEnvoiMail] = useLazyDemandeInscriptionEnvoiMailQuery();
  const [compteLoginExistRequest, { data: CompteLoginExistData }] = useLazyCompteLoginExistQuery();
  const [verifyCode] = useDemandeInscriptionVerifyCodeMutation();
  const [createDemandeInscription] = useFetchDemandeInscriptionCreateMutation();
  const { data: pays, isLoading: isPaysLoading } = useFetchPaysQuery();
  const { data: secteurActivites, isLoading: isSecteurActivitesLoading } = useFetchSecteurActiviteQuery();
  const { data: fonctions, isLoading: isFonctionsLoading } = useFetchFonctionQuery();
  const codeInputRefs = React.useRef<(HTMLInputElement | null)[]>([]);
  const previousPaysIdRef = React.useRef<number | undefined>(undefined);

  const formMethods = useForm<IFormInscription>({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(getInscriptionSchema(emailValidated, isFranceOrAssimile, sirenApiError)) as any,
    defaultValues: {
      Email: emailFromUrl || "",
      IdPays: undefined,
      Siren: "",
      RaisonSocialeSiren: "",
      RaisonSociale: "",
      Adresse: "",
      CodePostal: "",
      Ville: "",
      SiteInternet: "",
      IdCivilite: undefined,
      Nom: "",
      Prenom: "",
      Telephone: "",
      Fonction: "",
      GLNEditeur: "",
      MarquePrincipale: "",
      IdSecteurActivite: undefined,
      IndicatifISBN: "978",
      ISBN1: "",
      ISBN2: "",
      ISBN13EANComplet: "",
      GLNDiffuseur: "",
      Message: "",
      PiecesJustificatives: [],
      AcceptationCGU: false
    },
  });

  const { handleSubmit, watch, setValue, trigger } = formMethods;
  const watchedIdPays = watch("IdPays");
  const watchedSiren = watch("Siren");
  const watchedMessage = watch("Message");
  const watchedAcceptationCGU = watch("AcceptationCGU");

  // Utiliser le hook de validation du SIREN
  const { sirenApiError: sirenErrorFromHook } = useSirenValidation({
    siren: watchedSiren,
    isFranceOrAssimile,
    idDemande,
    setValue,
    trigger,
    showToasts: true
  });

  // Synchroniser le state local avec le hook
  React.useEffect(() => {
    setSirenApiError(sirenErrorFromHook);
  }, [sirenErrorFromHook]);

  // Vérifier si le pays est France ou assimilé
  React.useEffect(() => {
    if (watchedIdPays && pays?.Pays) {
      // Détecter si le pays a changé (pas lors du chargement initial ou validation email)
      const paysHasChanged = previousPaysIdRef.current !== undefined && previousPaysIdRef.current !== watchedIdPays;
      
      // Si le pays a changé, vider les champs SIREN et RaisonSocialeSiren (mais pas RaisonSociale)
      if (paysHasChanged) {
        setValue("Siren", "");
        setValue("RaisonSocialeSiren", "");
      }
      
      // Mettre à jour la référence du pays précédent
      previousPaysIdRef.current = watchedIdPays;
      
      const selectedPays = pays.Pays.find(p => p.Id === Number(watchedIdPays));
      const isFrance = selectedPays !== undefined && FRANCE_ET_ASSIMILES_IDS.includes(selectedPays.Id);
      setIsFranceOrAssimile(isFrance);
      
      // Si ce n'est pas la France après un changement, les champs sont déjà vidés ci-dessus
    }
  }, [watchedIdPays, pays, setValue]);

  // Mettre à jour le resolver quand emailValidated ou isFranceOrAssimile change
  React.useEffect(() => {
    formMethods.clearErrors();
    // Re-trigger validation
    formMethods.trigger();
  }, [emailValidated, isFranceOrAssimile, formMethods]);

  // Gérer la réponse de la vérification du compte existant
  React.useEffect(() => {
    if (CompteLoginExistData) {
      if (CompteLoginExistData.Exist && CompteLoginExistData.Actif) {
        // Compte actif → rediriger directement vers la connexion
        toast.info("Ce compte existe déjà. Redirection vers la page de connexion...");
        setTimeout(() => {
          login();
        }, 1500);
      } else if (CompteLoginExistData.Exist && !CompteLoginExistData.Actif) {
        // Compte existe mais non actif
        setEmailExists(true);
        setCompteActif(false);
        setShowCodeVerification(false);
        toast.warning("Votre compte est en attente de validation. Merci de vérifier votre boite email.");
      } else if (!CompteLoginExistData.Exist) {
        // Le compte n'existe pas → envoyer le code de vérification
        sendVerificationCode();
      }
    }
  }, [CompteLoginExistData]);

  const sendVerificationCode = async () => {
    try {
      const email = formMethods.getValues("Email");
      const result = await demandeInscriptionEnvoiMail(email).unwrap();
      
      if (result.Return_IdType_Message === 0 && result.Return_Message) {
        toast.error(result.Return_Message);
        return;
      }
      
      // Afficher le champ de code de vérification
      setEmailExists(false);
      setShowCodeVerification(true);
      toast.success("Un code de vérification a été envoyé à votre adresse email !");
    } catch (error: any) {
      console.error("Erreur lors de l'envoi du code :", error);
      const errorMessage = error?.data?.Return_Message || 
                          error?.message || 
                          "Une erreur est survenue. Veuillez réessayer.";
      toast.error(errorMessage);
    }
  };

  const login = useCallback(() => {
    keycloak?.login({ loginHint: formMethods.getValues("Email") });
  }, [formMethods]);

  const onSubmit: SubmitHandler<IFormInscription> = async (data) => {
    // Vérifier s'il y a une erreur API sur le SIREN
    if (sirenApiError) {
      toast.error(sirenApiError);
      return;
    }
    
    // Si l'email n'est pas encore validé, vérifier d'abord si le compte existe
    if (!emailValidated) {
      setIsLoading(true);
      try {
        // Vérifier si le compte existe déjà dans le système
        await compteLoginExistRequest({ Login: data.Email, Inscription: true });
      } finally {
        setIsLoading(false);
      }
    } else {
      // Si l'email est déjà validé, on crée la demande d'inscription
      setIsLoading(true);
      try {
        const demandeData = await transformFormDataToAPI(data, idDemande);
        const result = await createDemandeInscription(demandeData).unwrap();

        // Vérifier si l'API a retourné un message d'erreur
        if (result.Return_IdType_Message === 0 && result.Return_Message) {
          toast.error(result.Return_Message);
          return;
        }

        // Succès - Rediriger vers la page de statut
        toast.success("Votre demande d'inscription a été envoyée avec succès !");
        // Redirection vers la page d'inscription en cours
        navigate("/inscription-en-cours");
      } catch (error: any) {
        console.error("Erreur lors de la création de la demande d'inscription :", error);
        
        // Afficher le message d'erreur spécifique si disponible
        const errorMessage = error?.data?.Return_Message || 
                            error?.message || 
                            "Une erreur est survenue lors de l'envoi de votre demande. Veuillez réessayer.";
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleVerifyCode = async () => {
    const fullCode = verificationCode.join("");
    if (fullCode.length !== 6) {
      toast.error("Veuillez saisir un code à 6 chiffres");
      return;
    }

    setIsVerifying(true);
    try {
      const email = formMethods.getValues("Email");
      const result = await verifyCode({
        Email: email,
        CodeVerification: fullCode,
      }).unwrap();

      // Vérifier si l'API a retourné un message d'erreur
      if (result.Return_IdType_Message === 0 && result.Return_Message) {
        toast.error(result.Return_Message);
        return;
      }

      if (result.IsValid) {
        // Stocker l'ID de la demande s'il existe
        if (result.Demandeinscription?.Id) {
          setIdDemande(result.Demandeinscription.Id);
        }
        
        // Vérifier d'abord l'état de la demande (même si les données sont vides)
        // IdEtatDemandeInscription: 2 = Validée, 3 = En cours de traitement, 4 = En cours de traitement, 5 = Refusée
        if (result.Demandeinscription?.IdEtatDemandeInscription === 3 || 
            result.Demandeinscription?.IdEtatDemandeInscription === 4) {
          // Demande en cours de traitement - rediriger vers la page de statut
          toast.info("Votre demande d'inscription est en cours de traitement.");
          navigate("/inscription-en-cours");
          return;
        } else if (result.Demandeinscription?.IdEtatDemandeInscription === 5) {
          // Demande refusée - permettre de modifier et renvoyer
          toast.warning("Votre précédente demande a été refusée. Vous pouvez modifier vos informations et soumettre à nouveau.");
          if (result.Demandeinscription) {
            // Stocker le motif de refus pour l'afficher
            setMotifRefus(result.Demandeinscription.ExplicationRefus || null);
            prefillFormFromDemande(result.Demandeinscription, setValue);
          }
        } else if (result.Demandeinscription?.IdEtatDemandeInscription === 2) {
          // Demande validée - rediriger vers la page de validation par email
          toast.success("Votre demande a été validée ! Un e-mail vous a été envoyé.");
          navigate("/inscription-validation-email");
          return;
        }
        
        // Vérifier si une demande existante contient des données réelles
        if (result.Demandeinscription && 
            (result.Demandeinscription.IdPays || 
             result.Demandeinscription.RaisonSociale || 
             result.Demandeinscription.Nom)) {
          
          // Si ce n'est pas un refus, pré-remplir le formulaire
          if (result.Demandeinscription.IdEtatDemandeInscription !== 5) {
            prefillFormFromDemande(result.Demandeinscription, setValue);
            toast.success("Code vérifié avec succès ! Vos informations ont été récupérées.");
          }
        } else {
          // Nouvelle inscription ou demande refusée sans données
          console.log("Nouvelle inscription - aucune demande existante à pré-remplir");
          if (result.Demandeinscription?.IdEtatDemandeInscription !== 5) {
            toast.success("Code vérifié avec succès !");
          }
        }

        setEmailValidated(true);
        setShowCodeVerification(false);
      } else {
        toast.error(result.Return_Message || "Le code de vérification est incorrect");
      }
    } catch (error: any) {
      console.error("Erreur lors de la vérification du code :", error);     
      // Afficher le message d'erreur spécifique si disponible
      const errorMessage = error?.data?.Return_Message || 
                          error?.message || 
                          "Une erreur est survenue lors de la vérification du code. Veuillez réessayer.";
      toast.error(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    // Accepter uniquement les chiffres
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus sur le champ suivant
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Retour arrière : focus sur le champ précédent
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...verificationCode];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    
    setVerificationCode(newCode);
    
    // Focus sur le dernier champ rempli ou le suivant
    const nextIndex = Math.min(pastedData.length, 5);
    codeInputRefs.current[nextIndex]?.focus();
  };

  const onError = (errors: any) => {
    console.log("Erreurs:", errors);
  };

  const handleModifier = () => {
    setEmailExists(false);
    setEmailValidated(false);
    setShowCodeVerification(false);
    setVerificationCode(["", "", "", "", "", ""]);
    setSirenApiError(null);
    setMotifRefus(null);
    
    // Réinitialiser tous les champs du formulaire sauf l'email
    const currentEmail = formMethods.getValues("Email");
    formMethods.reset({
      Email: currentEmail,
      IdPays: undefined,
      Siren: "",
      RaisonSocialeSiren: "",
      RaisonSociale: "",
      Adresse: "",
      CodePostal: "",
      Ville: "",
      SiteInternet: "",
      IdCivilite: undefined,
      Nom: "",
      Prenom: "",
      Telephone: "",
      Fonction: "",
      GLNEditeur: "",
      MarquePrincipale: "",
      IdSecteurActivite: undefined,
      IndicatifISBN: "978",
      ISBN1: "",
      ISBN2: "",
      ISBN13EANComplet: "",
      GLNDiffuseur: "",
      Message: "",
      PiecesJustificatives: [],
      AcceptationCGU: false,
    });
  };

  const handleOpenModalConvention = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowModalConvention(true);
  };

  const handleCheckboxClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const isChecked = formMethods.getValues("AcceptationCGU");
    
    if (isChecked) {
      // Si déjà coché, permettre de décocher
    } else {
      // Si pas coché, TOUJOURS empêcher le clic direct et ouvrir le modal
      e.preventDefault();
      setShowModalConvention(true);
    }
  };

  const handleCloseModalConvention = () => {
    setShowModalConvention(false);
  };

  const handleAcceptConvention = () => {
    formMethods.setValue("AcceptationCGU", true, { shouldValidate: true, shouldTouch: true });
    setShowModalConvention(false);
  };

  return (
    <>
      <div className="row pt-3 pb-5">
        <div className="col col-md-10 offset-md-1 col-12 mb-3">
          <div className="titre text-center">Inscription Editeurs Pros</div>
          
        </div>
      </div>

      <div className="row">
        <div className="col col-md-6 offset-md-3 col-12">
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
              <CpntForm>
                <CpntFormSection>
                  <CpntEmailVerification
                    formMethods={formMethods}
                    emailExists={emailExists}
                    compteActif={compteActif}
                    emailValidated={emailValidated}
                    showCodeVerification={showCodeVerification}
                    isSubmitting={isLoading}
                    onModifier={handleModifier}
                  />
                  
                  {showCodeVerification && (
                    <CpntCodeVerification
                      verificationCode={verificationCode}
                      codeInputRefs={codeInputRefs}
                      isVerifying={isVerifying}
                      onCodeChange={handleCodeChange}
                      onCodeKeyDown={handleCodeKeyDown}
                      onCodePaste={handleCodePaste}
                      onVerifyCode={handleVerifyCode}
                    />
                  )}

                </CpntFormSection>

                {emailValidated && (
                  <CpntInscriptionForm
                    isPaysLoading={isPaysLoading}
                    pays={pays}
                    isFranceOrAssimile={isFranceOrAssimile}
                    isSecteurActivitesLoading={isSecteurActivitesLoading}
                    secteurActivites={secteurActivites}
                    isFonctionsLoading={isFonctionsLoading}
                    fonctions={fonctions}
                    watchedMessage={watchedMessage ?? ""}
                    watchedAcceptationCGU={watchedAcceptationCGU}
                    sirenApiError={sirenApiError}
                    motifRefus={motifRefus}
                    handleOpenModalConvention={handleOpenModalConvention}
                    handleCheckboxClick={handleCheckboxClick}
                    onRetour={() => {
                      setEmailValidated(false);
                      window.scrollTo(0, 0);
                    }}
                  />
                )}
              </CpntForm>
            </form>
          </FormProvider>
        </div>
      </div>

      <CpntModalConvention
        show={showModalConvention}
        onClose={handleCloseModalConvention}
        onAccept={handleAcceptConvention}
      />
    </>
  );
};

export default PageInscription;
